import React from 'react';
import { Document, Page, Text } from '@react-pdf/renderer';
import { ReportData, FindingConfig } from '../types/report.types';
import { SectionPageProvider } from './SectionPageContext';
import { filterReportDataByRunId, getFindingStableKey, isFindingActuallyOpen } from '../utils/findingsUtils';
import { processDisclaimerContent, registerReportFonts, sortFindings } from '../utils/reportSharedUtils';

// V2 Section Components
import CoverPageV2 from './sections/v2/CoverPageV2';
import ExecutiveSummaryPageV2 from './sections/v2/ExecutiveSummaryPageV2';
import ScopePageV2 from './sections/v2/ScopePageV2';
import MethodologyPageV2 from './sections/v2/MethodologyPageV2';
import OwaspTop10PageV2 from './sections/v2/OwaspTop10PageV2';
import SeverityRatingPageV2 from './sections/v2/SeverityRatingPageV2';
import FindingsSummaryPageV2 from './sections/v2/FindingsSummaryPageV2';
import DetailedFindingsBySeverityPageV2 from './sections/v2/DetailedFindingsBySeverityPageV2';
import RemediationRoadmapPageV2 from './sections/v2/RemediationRoadmapPageV2';
import ConclusionToolsPageV2 from './sections/v2/ConclusionToolsPageV2';
import AppendixPageV2 from './sections/v2/AppendixPageV2';
import CertificatePageV2 from './sections/v2/CertificatePageV2';
import AboutUsPageV2 from './sections/v2/AboutUsPageV2';

// Register fonts once globally
registerReportFonts();

const processFindingsForPDF = (reportData: ReportData, passedProcessedFindings?: Record<string, any[]> | null) => {
  let parsedConfig: any = {
    globalFields: {
      description: true,
      instructions: true,
      impact: true,
      fix: true,
      evidence: true
    },
    individualFindings: {}
  };
  if (reportData.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {}
    } else {
      parsedConfig = reportData.finding_config;
    }
  }
  const config: FindingConfig = parsedConfig;

  const detailedFindings = (reportData.detailed_findings || []).map((f, idx) => ({
    ...f,
    _stableKey: getFindingStableKey(f, idx)
  }));

  const sortedFindings = sortFindings(detailedFindings);

  const filteredFindings = sortedFindings.filter(f => {
    const idStr = (f as any)._stableKey;
    const indConfig = config.individualFindings?.[idStr];
    if (indConfig) {
      return indConfig.included;
    }
    return isFindingActuallyOpen(reportData, f);
  });

  const grouped: Record<string, any[]> = {
    CRITICAL: [],
    HIGH: [],
    MEDIUM: [],
    LOW: [],
    INFO: []
  };

  filteredFindings.forEach(f => {
    const sev = (f.severity_category || 'INFO').toUpperCase();
    if (grouped[sev]) {
      grouped[sev].push(f);
    } else {
      grouped.INFO.push(f);
    }
  });

  return { groupedFindings: grouped, config };
};

interface FullReportTemplatePDF2Props {
  reportData: ReportData;
  pieChartImage?: string;
  barChartImage?: string;
  processedFindings?: Record<string, any[]> | null;
  processing?: boolean;
}

const FullReportTemplatePDF2Content: React.FC<FullReportTemplatePDF2Props> = ({
  reportData: rawReportData,
  pieChartImage,
  barChartImage,
  processedFindings: passedProcessedFindings,
  processing,
}) => {
  const reportData = React.useMemo(() => filterReportDataByRunId(rawReportData) || rawReportData, [rawReportData]);
  const { groupedFindings } = processFindingsForPDF(reportData, passedProcessedFindings);
  const activeFindings = passedProcessedFindings || groupedFindings;
  const hasOpenDetailed = Object.values(activeFindings).some(arr => (arr || []).length > 0);

  if (processing) {
    return (
      <Document>
        <Page>
          <Text>Loading...</Text>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      {/* 1. Cover Page */}
      <CoverPageV2 reportData={reportData} />

      {/* 2. Executive Summary */}
      <ExecutiveSummaryPageV2
        reportData={reportData}
        pieChartImage={pieChartImage}
        barChartImage={barChartImage}
      />

      {/* 3. Scope of Assessment */}
      <ScopePageV2 reportData={reportData} />

      {/* 4. Methodology */}
      <MethodologyPageV2 reportData={reportData} />

      {/* 5. OWASP Top 10 Mapping */}
      <OwaspTop10PageV2 reportData={reportData} />

      {/* 6. Severity Rating Definitions */}
      <SeverityRatingPageV2 reportData={reportData} />

      {/* 7. Findings Summary (Charts + severity table) */}
      <FindingsSummaryPageV2
        reportData={reportData}
        pieChartImage={pieChartImage}
        barChartImage={barChartImage}
      />

      {/* 8. Detailed Findings by Severity */}
      {hasOpenDetailed && (() => {
        let firstFound = false;
        return ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].map(severity => {
          const list = activeFindings?.[severity] || [];
          if (list.length === 0) return null;
          const isFirst = !firstFound;
          firstFound = true;
          return (
            <DetailedFindingsBySeverityPageV2
              key={severity}
              severity={severity.charAt(0) + severity.slice(1).toLowerCase()}
              findings={list}
              documentNumber={reportData.document_number}
              reportData={reportData}
              sectionId={`DetailedFindings_Full_V2_${severity}`}
              showMainTitle={isFirst}
            />
          );
        });
      })()}

      {/* 9. Remediation Roadmap */}
      <RemediationRoadmapPageV2 reportData={reportData} />

      {/* 10. Conclusion & Tools Approach */}
      <ConclusionToolsPageV2 reportData={reportData} />

      {/* 11. Appendix */}
      <AppendixPageV2 reportData={reportData} />

      {/* 12. Certificate of Attestation */}
      <CertificatePageV2 reportData={reportData} />

      {/* About Us Page */}
      <AboutUsPageV2 />
    </Document>
  );
};

const FullReportTemplatePDF2: React.FC<FullReportTemplatePDF2Props> = (props) => (
  <SectionPageProvider>
    <FullReportTemplatePDF2Content {...props} />
  </SectionPageProvider>
);

export default FullReportTemplatePDF2;
